// src/pages/dashboard/Settings.js
import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Shield, User, Calendar, DollarSign, Bell, LifeBuoy, Globe, CreditCard, ChevronRight, BadgeCheck, Plug2, FileText } from 'lucide-react';

const Settings = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm mb-6" aria-label="Breadcrumb">
          <Link to="/dashboard/practitioner" className="text-purple-600 hover:text-purple-700 font-medium transition-colors">
            Dashboard
          </Link>
          <span className="text-gray-400">/</span>
          <span className="text-gray-900 font-medium">Settings</span>
        </nav>

        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Settings</h1>
          <p className="text-gray-600">Manage your account, business, and preferences</p>
        </div>

        {/* Settings Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Account & Security */}
          <button
            onClick={() => navigate('/dashboard/settings/account')}
            className="w-full text-left p-6 bg-white border-2 border-gray-200 rounded-xl hover:border-purple-400 hover:shadow-md transition-all"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Shield className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-1">Account & Security</h3>
                  <p className="text-sm text-gray-600">Login credentials, security, and account management</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </div>
          </button>

          {/* Public Profile */}
          <button
            onClick={() => navigate('/dashboard/settings/profile')}
            className="w-full text-left p-6 bg-white border-2 border-gray-200 rounded-xl hover:border-purple-400 hover:shadow-md transition-all"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <User className="w-6 h-6 text-indigo-600" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-1">Public Profile</h3>
                  <p className="text-sm text-gray-600">Your bio, photo, and public information</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </div>
          </button>

          {/* Availability & Scheduling */}
          <button
            onClick={() => navigate('/dashboard/settings/availability')}
            className="w-full text-left p-6 bg-white border-2 border-gray-200 rounded-xl hover:border-purple-400 hover:shadow-md transition-all"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Calendar className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-1">Availability & Scheduling</h3>
                  <p className="text-sm text-gray-600">Working hours, time off, and booking preferences</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </div>
          </button>

          {/* Business & Payments */}
          <button
            onClick={() => navigate('/dashboard/settings/business')}
            className="w-full text-left p-6 bg-white border-2 border-gray-200 rounded-xl hover:border-purple-400 hover:shadow-md transition-all"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <DollarSign className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-1">Business & Payments</h3>
                  <p className="text-sm text-gray-600">Business info, Stripe connection, and payouts</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </div>
          </button>

          {/* Policies */}
          <button
            onClick={() => navigate('/dashboard/settings/policies')}
            className="w-full text-left p-6 bg-white border-2 border-gray-200 rounded-xl hover:border-purple-400 hover:shadow-md transition-all"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-rose-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <FileText className="w-6 h-6 text-rose-600" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-1">Policies</h3>
                  <p className="text-sm text-gray-600">Cancellation, privacy, and terms of service</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </div>
          </button>

          {/* Plan & Billing */}
          <button
            onClick={() => navigate('/dashboard/subscription')}
            className="w-full text-left p-6 bg-white border-2 border-gray-200 rounded-xl hover:border-purple-400 hover:shadow-md transition-all"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-violet-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <CreditCard className="w-6 h-6 text-violet-600" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-1">Plan & Billing</h3>
                  <p className="text-sm text-gray-600">Manage your subscription plan and billing</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </div>
          </button>

          {/* Connect Your Website */}
          <button
            onClick={() => navigate('/dashboard/settings/connect-website')}
            className="w-full text-left p-6 bg-white border-2 border-gray-200 rounded-xl hover:border-purple-400 hover:shadow-md transition-all"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Globe className="w-6 h-6 text-cyan-600" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-1">Connect Your Website</h3>
                  <p className="text-sm text-gray-600">Add a booking button to your existing website or copy your profile link</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </div>
          </button>

          {/* Integrations */}
          <button
            onClick={() => navigate('/dashboard/settings/integrations')}
            className="w-full text-left p-6 bg-white border-2 border-gray-200 rounded-xl hover:border-purple-400 hover:shadow-md transition-all"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Plug2 className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-1">Integrations</h3>
                  <p className="text-sm text-gray-600">Connect Zoom, Stripe, social accounts, and more</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </div>
          </button>

          {/* Trust & Badges */}
          <button
            onClick={() => navigate('/dashboard/settings/verification')}
            className="w-full text-left p-6 bg-white border-2 border-gray-200 rounded-xl hover:border-purple-400 hover:shadow-md transition-all"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <BadgeCheck className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-1">Trust & Badges</h3>
                  <p className="text-sm text-gray-600">Verification status, earned badges, and quality metrics</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </div>
          </button>

          {/* Notification Settings */}
          <button
            onClick={() => navigate('/dashboard/settings/notifications')}
            className="w-full text-left p-6 bg-white border-2 border-gray-200 rounded-xl hover:border-purple-400 hover:shadow-md transition-all"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Bell className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-1">Notifications</h3>
                  <p className="text-sm text-gray-600">Control how and when you receive notifications</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </div>
          </button>

          {/* Help & Support */}
          <button
            onClick={() => navigate('/dashboard/settings/support')}
            className="w-full text-left p-6 bg-white border-2 border-gray-200 rounded-xl hover:border-purple-400 hover:shadow-md transition-all"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <LifeBuoy className="w-6 h-6 text-teal-600" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-1">Help & Support</h3>
                  <p className="text-sm text-gray-600">Get help, FAQs, and contact our support team</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Settings;
